<script lang="ts">
  import Form from "../../components/Form.svelte";
  import RadioGroup from "../../components/RadioGroup.svelte";
  import SharepointFileInput from "../../components/SharepointFileInput.svelte";
  import Caret from "~icons/ph/caret-down";
  import CalendarCheck from "~icons/ph/calendar-check";
  import { domId } from "../../lib/dom";

  let {
    action,
    errors,
    clientFirstName,
    createUploadSessionUrl,
    uploadAttachmentExtensions,
  }: {
    action: string;
    errors: { [key: string]: string } | null;
    clientFirstName: string;
    createUploadSessionUrl: string;
    uploadAttachmentExtensions: string[];
  } = $props();

  let details = $state(null);
  let hasDocument = $state("yes");
  let wantsHelp = $state("no");

  function hideDetails() {
    if (details) details.open = false;
  }

  const closeDialogId = domId("close-dialog");
</script>

<Form method="patch" {action} class="ui-rhythm">
  <h2 class="ui-title">Upload letters of administration</h2>

  <p>
    Since {clientFirstName} didn’t have a will, this document proves you’re legally appointed to handle their legal and financial matters once deceased.
  </p>

  <details class="ui-details">
    <summary>
      <Caret />What are letters of administration and how do I get them?
    </summary>

    <p>
      Letters of administration are legal documents issued by a Probate Registry authorising a person to manage
      the estate of someone who passed away without a will. You can apply for them through the
      <a href="https://www.gov.uk/applying-for-probate/if-theres-not-a-will" target="_blank" rel="noreferrer" class="link text-blue-600">
        GOV.UK website
      </a>
      to act as an administrator, following a strict legal priority order (spouse/civil partners, then children etc).
      You can use the
      <a href="https://www.gov.uk/inherits-someone-dies-without-will" target="_blank" rel="noreferrer" class="link text-blue-600">
        Inheritance Checker
      </a>
      to check priority order. Please note that if the estate’s value exceeds £5,000, there is a flat fee of £300 payable
      for obtaining the relevant grant. For estates valued below £5,000, no application fee applies.
    </p>
  </details>

  <fieldset class="ui-fieldset">
    <legend>Do you have the letters of administration?</legend>

    <RadioGroup
      name="has_document"
      options={[
        ["Yes", "yes"],
        ["Not yet, but I’ve started the process", "not_yet"],
        ["No, I haven’t started yet", "no"],
      ]}
      bind:group={hasDocument}
      required />
  </fieldset>

  <div class="ui-content-options mt-6">
    <fieldset class="ui-fieldset" class:invisible={hasDocument !== "yes"}>
      <legend>Upload letters of administration</legend>

      <div>
        <p class="ui-alert ui-alert-info pb-4 -mb-2.5">Please provide clear and legible photo(s) of the letters of administration. Add all pages before sending.</p>
        <SharepointFileInput
          name="letters_of_administration"
          accept={uploadAttachmentExtensions}
          {createUploadSessionUrl}
          multiple
          required={hasDocument === "yes"} />
      </div>

      {#if errors?.letters_of_administration}<div class="ui-error">{errors.letters_of_administration}</div>{/if}
    </fieldset>

    <div class:invisible={hasDocument === "yes"}>
      <fieldset class="ui-fieldset">
        <legend>If you’d like help with these documents, a law firm we’re partnering with can reach out:</legend>

        <div class="ui-option-container">
          <label class="flex flex-row gap-2 items-start">
            <input
              type="radio"
              class="radio"
              name="wants_help"
              value="yes"
              bind:group={wantsHelp}
              onchange={hideDetails}
              required
            >
            Yes, please reach out to me
          </label>

          <details class="ui-details mt-4" bind:this={details}>
            <summary><Caret /> How does this work?</summary>
            <ul class="ui-list pl-6">
              <li>We’ll refer you to a specialist probate firm, Private Client Solicitors, who can help with estate administration, such as grant of probate, or letters of administration</li>
              <li>They offer an initial consultation free of charge, where they can discuss any subsequent fees directly with you</li>
              <li>We don’t receive any fee or commission for this referral, and you’re free to choose any adviser you wish</li>
            </ul>
          </details>
        </div>

        <div class="ui-option-container">
          <label class="flex flex-row gap-2 items-start">
            <input
              type="radio"
              name="wants_help"
              value="no"
              class="radio"
              bind:group={wantsHelp}
              onchange={hideDetails}
              required
            >
            No, I can do it myself
          </label>
        </div>
      </fieldset>

      <div class="ui-alert ui-alert-info" class:invisible={wantsHelp !== "no"}>
        <CalendarCheck />
        <p>
          Take all the time you need, and we’ll check back in <b>{hasDocument === "not_yet" ? "two weeks" : "a month"}</b>.
          Until then, feel free to use our resources above to find this document. When you find it, you can upload it here.
        </p>
      </div>
    </div>
  </div>

  {#if errors?.general}<div class="ui-error">{errors.general}</div>{/if}

  <div class="floating-footer">
    <button class="btn btn-ghost" form={closeDialogId}>Cancel</button>
    <button class="btn btn-primary" type="submit" data-viewer-mode>{hasDocument === "yes" ? "Send" : "Finish"}</button>
  </div>
</Form>

<form method="dialog" id={closeDialogId}></form>
